import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref, useContext, useCssModule, watch, } from '@nuxtjs/composition-api';
import { ROWS_LIMIT, MIN_ROWS_NUMBER_FOR_SORTING, COLUMNS_LIMIT_DEFAULT, COLUMNS_LIMIT_SPORT, COLUMNS_LIMIT_ASSETS, } from './';
import { isProfileMan } from '~/utils';
export default defineComponent({
    name: 'ArticleRating',
    props: {
        article: {
            required: true,
            type: Object,
        },
        isSport: {
            default: false,
            type: Boolean,
        },
        searchQuery: {
            default: '',
            type: String,
        },
    },
    setup(props) {
        /**
         * Composables.
         */
        const context = useContext();
        const css = useCssModule();
        const viewport = context.$viewport;
        let fieldsEntries = JSON.parse(JSON.stringify(props.article.rating.field_values));
        /**
         * Root element.
         */
        const rootEl = ref();
        /**
         * Header anchor.
         */
        const headerAnchor = reactive({
            observer: undefined,
            isPinned: false,
        });
        /**
         * Header anchor element.
         */
        const headerAnchorElement = ref();
        /**
         * Rating is divided into groups or not
         */
        const isGrouped = computed(() => Boolean((props.article.rating || {}).grouped));
        /**
         * Rating is embedded in the article or not
         */
        const isInset = computed(() => (props.article.rating || {})?.isInset);
        /**
         * Rating body visible count.
         */
        const ratingBodyVisible = ref(isGrouped.value ? Infinity : ROWS_LIMIT);
        /**
         * Current sorting by.
         */
        const sortingBy = ref({});
        /**
         * Should show rating
         */
        const shouldShowRatingHead = computed(() => props.isSport || viewport.isGreaterThan('tablet'));
        /**
         * Should sort rating
         */
        const shouldSort = computed(() => Object.keys(fieldsEntries).length >= MIN_ROWS_NUMBER_FOR_SORTING);
        /**
         * With person's assets
         */
        // const withAssets = computed(() => Boolean((props.article.rating || {}).showassets))
        const withAssets = ref(false);
        /**
         * Position is not hidden
         */
        const withPosition = ref(false);
        /**
         * Columns limit
         */
        const columnsLimit = computed(() => {
            if (props.isSport) {
                return COLUMNS_LIMIT_SPORT;
            }
            if (withAssets.value) {
                return COLUMNS_LIMIT_ASSETS;
            }
            return COLUMNS_LIMIT_DEFAULT;
        });
        /**
         * Rating head.
         */
        const ratingHead = computed(() => {
            const { fields = [], profiletid = '' } = props.article.rating;
            if (!fields.length) {
                return {};
            }
            let head = {};
            const fieldsKeys = Object.values(fields).map((f) => String(f.cf_id));
            fieldsKeys.splice(1, 0, 'name');
            for (const fieldKey in fieldsKeys) {
                const field = fields[+fieldKey > 0 ? +fieldKey - 1 : 0];
                if (!field || shouldHideField(field)) {
                    continue;
                }
                const isSortedByDefault = field.is_key === '1';
                const prefix = field.pre_str;
                const suffix = field.sub_str;
                let text = field.title.replace(/,/g, '');
                if (Number(fieldKey) === 0) {
                    text = '#';
                    withPosition.value = true;
                }
                head[`$${fieldsKeys[fieldKey]}`] = {
                    isSortable: shouldSort.value,
                    isSortedByDefault,
                    prefix,
                    suffix,
                    text,
                };
            }
            let text = 'Название';
            if (props.isSport) {
                text = 'Команда';
            }
            else if (isProfileMan(profiletid)) {
                text = 'Имя';
            }
            const nameField = {
                isSortable: shouldSort.value,
                isSortedByDefault: false,
                text,
            };
            if (withPosition.value) {
                head.$name = nameField;
            }
            else {
                head = {
                    $name: nameField,
                    ...head,
                };
            }
            return head;
        });
        /**
         * Rating body.
         */
        const ratingBody = computed(() => {
            const body = [];
            const columnsReference = JSON.stringify(Object.keys(ratingHead.value).reduce((acc, key) => {
                acc[key] = '';
                return acc;
            }, {}));
            updateRawPositions();
            for (const entryKey in fieldsEntries) {
                const fieldEntry = fieldsEntries[entryKey];
                const columns = JSON.parse(columnsReference);
                for (const fieldValue of fieldEntry.values) {
                    if (!fieldValue || shouldHideField(fieldValue)) {
                        continue;
                    }
                    const { prefix = '', suffix = '' } = ratingHead.value[`$${fieldValue.cf_id}`] || {};
                    let value = '';
                    if (prefix) {
                        value += `${prefix} `;
                    }
                    const isFloat = !!Number(fieldValue?.is_float);
                    fieldValue.float_val = String(fieldValue.float_val || '').trim();
                    fieldValue.string_val = String(fieldValue.string_val || '').trim();
                    if (isFloat) {
                        value += fieldValue.float_val || '—';
                    }
                    else {
                        value += fieldValue.string_val || '—';
                    }
                    if (suffix) {
                        value += ` <small>${suffix}</small>`;
                    }
                    columns[`$${fieldValue.cf_id}`] = value;
                }
                columns.$name = String(fieldEntry?.profile?.title || '').trim();
                body.push({
                    columns,
                    profile: fieldEntry.profile,
                });
            }
            body.sort((a, b) => {
                const [firstHead] = Object.keys(ratingHead.value) || [];
                if (!firstHead) {
                    return -1;
                }
                if (firstHead === '$name') {
                    return a.columns[firstHead].localeCompare(b.columns[firstHead]);
                }
                return Number(a.columns[firstHead]) - Number(b.columns[firstHead]);
            });
            return body;
        });
        /**
         * Rating body chunk.
         */
        const ratingBodyChunk = computed(() => {
            ratingBody.value.sort((a, b) => {
                const [key] = Object.keys(sortingBy.value);
                if (!key) {
                    return -1;
                }
                const positionKey = getPositionKey();
                const positionA = a.columns[positionKey];
                const positionB = b.columns[positionKey];
                let valueA = a.columns[key].replace('$ ', '');
                let valueB = b.columns[key].replace('$ ', '');
                if (props.isSport && !['$name', positionKey].includes(key)) {
                    valueA = valueA.split(' ').at(-1);
                    valueB = valueB.split(' ').at(-1);
                }
                const numberA = parseFloat(valueA);
                const numberB = parseFloat(valueB);
                switch (sortingBy.value[key]) {
                    case true: {
                        if (valueA === valueB || numberA === numberB) {
                            return positionB - positionA;
                        }
                        if (isNaN(numberA)) {
                            return valueA.localeCompare(valueB);
                        }
                        return numberA - numberB;
                    }
                    case false: {
                        if (valueA === valueB || numberA === numberB) {
                            return positionA - positionB;
                        }
                        if (isNaN(numberB)) {
                            return valueB.localeCompare(valueA);
                        }
                        return numberB - numberA;
                    }
                    default: {
                        return -1;
                    }
                }
            });
            if (!props.searchQuery) {
                return ratingBody.value.slice(0, ratingBodyVisible.value);
            }
            return ratingBody.value.filter(({ columns }) => {
                const matches = [];
                const query = props.searchQuery.toLowerCase();
                for (const columnKey in columns) {
                    const value = columns[columnKey].toLowerCase();
                    const isMatching = value.includes(query);
                    if (isMatching) {
                        matches.push(true);
                    }
                }
                return matches.length;
            });
        });
        /**
         * Handles back to top
         */
        function handleBackTop() {
            if (!rootEl.value) {
                return;
            }
            window.scrollTo({
                behavior: 'smooth',
                top: rootEl.value.offsetTop - 56,
            });
        }
        /**
         * Handles more button "click".
         */
        function handleMoreClick() {
            const diff = ratingBody.value.length - ratingBodyVisible.value;
            ratingBodyVisible.value =
                diff >= ROWS_LIMIT ? ratingBodyVisible.value + ROWS_LIMIT : ratingBodyVisible.value + diff;
        }
        /**
         * Handles "sortingUpdate" event.
         */
        function handleSortingUpdate(key) {
            sortingBy.value = {
                [key]: !sortingBy.value[key],
            };
        }
        /**
         * Should hide field
         */
        function shouldHideField(field) {
            // const title = field.title || field.field_title || ''
            // if (['позиция', 'место'].includes(title.trim().toLowerCase())) {
            //   return false
            // }
            const isHidden = field.isHidden || field?.is_hidden === '1' || false;
            return +isHidden;
        }
        /**
         * Get default sort key
         */
        function getDefaultSortKey(withFallback = false) {
            let sortingColumn = Object.entries(ratingHead.value).find(([_, { isSortedByDefault: is }]) => is);
            if (withFallback && !sortingColumn) {
                sortingColumn = Object.keys(ratingHead.value) || [];
            }
            return sortingColumn?.[0] || '';
        }
        /**
         * Get position key
         */
        function getPositionKey() {
            const column = Object.entries(ratingHead.value).find(([_, { text }]) => text === '#');
            return column?.[0] || '';
        }
        /**
         * Updates rating positions
         */
        function updateRawPositions() {
            let defaultSortKey = getDefaultSortKey();
            const positionKey = getPositionKey();
            if (!defaultSortKey || defaultSortKey === positionKey) {
                return;
            }
            defaultSortKey = defaultSortKey.replace('$', '');
            fieldsEntries = Object.entries(fieldsEntries)
                .sort(([, { values: valuesA }], [, { values: valuesB }]) => {
                const { float_val: floatValA = 0, string_val: stringValA = '0' } = valuesA.find(({ cf_id: id }) => Number(id) === Number(defaultSortKey)) || {};
                const { float_val: floatValB = 0, string_val: stringValB = '0' } = valuesB.find(({ cf_id: id }) => Number(id) === Number(defaultSortKey)) || {};
                const valA = floatValA || Number(stringValA);
                const valB = floatValB || Number(stringValB);
                if (isNaN(valA) || isNaN(valB)) {
                    return -1;
                }
                return valB - valA;
            })
                .reduce((entries, [key, { profile, values }], i) => {
                const position = i + 1;
                for (const value of values) {
                    if (value.field_title === 'Позиция') {
                        value.float_val = position;
                        break;
                    }
                }
                entries[key] = {
                    position,
                    profile,
                    values,
                };
                return entries;
            }, {});
        }
        onBeforeUnmount(() => {
            headerAnchor.observer?.disconnect();
        });
        onMounted(() => {
            watch(() => viewport.breakpoint, () => {
                const defaultSortKey = getDefaultSortKey(true);
                sortingBy.value = {
                    [defaultSortKey]: !props.isSport,
                };
            }, {
                immediate: true,
            });
            if (!headerAnchorElement.value) {
                return;
            }
            headerAnchor.observer = new IntersectionObserver(([entry]) => {
                headerAnchor.isPinned = entry.intersectionRatio < 1;
            }, {
                threshold: 1,
            });
            headerAnchor.observer.observe(headerAnchorElement.value);
        });
        return {
            css,
            columnsLimit,
            headerAnchor,
            headerAnchorElement,
            handleBackTop,
            handleMoreClick,
            handleSortingUpdate,
            isGrouped,
            isInset,
            ratingBody,
            ratingBodyChunk,
            ratingBodyVisible,
            ratingHead,
            rootEl,
            sortingBy,
            shouldShowRatingHead,
            shouldSort,
            withAssets,
            withPosition,
        };
    },
});
